/**
 * Fullscreen the player+chat row, on the browsers that can.
 *
 * The Twitch / YouTube / Kick embeds each ship their own fullscreen button, but
 * it fullscreens ONLY the platform iframe — the side-by-side ChatPanel (a
 * separate cross-origin iframe) is lost. This fullscreens the whole ROW instead
 * so chat stays alongside the video, and keeps its own state in sync with the
 * native Escape-key exit via `fullscreenchange`.
 *
 * Split out of `StreamPanel.tsx`: self-contained browser-API plumbing, and the
 * file was past the 400-line ceiling.
 */
import { useEffect, useRef, useState } from "react";

export interface FullscreenRow {
    /** Attach to the element that should go fullscreen. */
    ref: React.RefObject<HTMLDivElement>;
    /** True only when THIS row owns the fullscreen, not any embed inside it. */
    isFullscreen: boolean;
    /** False where no Fullscreen API exists — hide the control rather than offer a dead one. */
    supported: boolean;
    toggle: () => void;
}

export function useFullscreenRow(): FullscreenRow {
    const ref = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    /*
     * The Fullscreen API is unprefixed on desktop + Android, webkit-prefixed on
     * older Safari, and ENTIRELY absent for arbitrary elements on iOS Safari
     * (iPhone only fullscreens <video> via webkitEnterFullscreen). Calling the
     * unprefixed element.requestFullscreen() there threw "requestFullscreen is
     * not a function" for every iOS viewer (DEVOUR-WEB-3Q5). Detect support,
     * route through whichever API exists, and hide the control when none do.
     */
    const supported = typeof document !== "undefined" &&
        Boolean(document.documentElement.requestFullscreen ||
            (document.documentElement as HTMLElement & { webkitRequestFullscreen?: unknown }).webkitRequestFullscreen);

    useEffect(() => {
        if (!supported) return;
        const onChange = () => {
            const fsEl = document.fullscreenElement ??
                (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ?? null;
            setIsFullscreen(fsEl === ref.current);
        };
        document.addEventListener("fullscreenchange", onChange);
        document.addEventListener("webkitfullscreenchange", onChange);
        return () => {
            document.removeEventListener("fullscreenchange", onChange);
            document.removeEventListener("webkitfullscreenchange", onChange);
        };
    }, [supported]);

    const toggle = () => {
        /*
         * Branch on OUR isFullscreen (does the row own the fullscreen?), not the
         * ambient document.fullscreenElement — the embed iframes ship their own
         * fullscreen button, so document.fullscreenElement can point at the
         * iframe. Keying off that would make our button exit the embed's
         * fullscreen instead of entering our player+chat one.
         */
        if (isFullscreen) {
            const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
            const exit = doc.exitFullscreen ?? doc.webkitExitFullscreen;
            if (exit) void exit.call(doc);
        } else {
            const el = ref.current as
                (HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> | void }) | null;
            if (!el) return;
            const request = el.requestFullscreen ?? el.webkitRequestFullscreen;
            if (request) void request.call(el);
        }
    };

    return { ref, isFullscreen, supported, toggle };
}
